import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays the selected variant's option value as text.
 * Mirrors product-sku.js: it listens for variant update events and swaps its
 * text, so the line stays correct when a swatch is clicked on a product card.
 *
 * The option to display is chosen by position (1, 2 or 3) via data-option-position,
 * matching the `swatch_option_position` vocabulary on the swatches block.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} variantLine - The span element that displays the option value.
 *
 * @extends {Component<Refs>}
 */
class ProductVariantLineComponent extends Component {
  requiredRefs = ['variantLine'];

  connectedCallback() {
    super.connectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.addEventListener(ThemeEvents.variantUpdate, this.updateVariantLine);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    if (!target) return;
    target.removeEventListener(ThemeEvents.variantUpdate, this.updateVariantLine);
  }

  /**
   * Updates the displayed option value.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateVariantLine = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;
    if (!variant) return;

    // `options` is the variant's option values in order; position 1 maps to index 0.
    // Fall back to option1/2/3, which the variant JSON always carries, in case
    // `options` is absent from the payload.
    const index = Number(this.dataset.optionPosition || '1') - 1;
    const value = Array.isArray(variant.options) ? variant.options[index] : variant[`option${index + 1}`];

    if (value) {
      this.style.display = 'block';
      this.refs.variantLine.textContent = value;
    } else {
      // Hide the whole line rather than leave an empty row in the card.
      this.style.display = 'none';
      this.refs.variantLine.textContent = '';
    }
  };
}

if (!customElements.get('product-variant-line-component')) {
  customElements.define('product-variant-line-component', ProductVariantLineComponent);
}
